import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';

/** Minimal shape a country option needs. `CountryData` is structurally compatible. */
export interface CountrySelectItem {
  code: string;
  name: string;
  dialCode?: string;
}

/**
 * Selector de país con banderas SVG (flag-icons) y búsqueda.
 * Reemplaza al `<select>` nativo, que no puede renderizar SVG dentro de `<option>`.
 */
@Component({
  selector: 'app-country-select',
  standalone: true,
  imports: [CommonModule, FormsModule, ScrollingModule],
  templateUrl: './country-select.component.html',
})
export class CountrySelectComponent {
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Alto de cada fila del dropdown (px), debe coincidir con la clase h-9 del botón. */
  readonly ITEM_SIZE = 36;

  /** Alto del viewport virtual: hasta 6 filas visibles, o menos si hay pocos resultados. */
  get viewportHeight(): number {
    return Math.min(this.filtered.length, 6) * this.ITEM_SIZE;
  }

  @Input() countries: CountrySelectItem[] = [];
  @Input() value = '';
  @Input() placeholder = 'País';
  /** Muestra un botón ✕ para limpiar la selección (útil como filtro con opción "Todos"). */
  @Input() clearable = false;
  @Output() valueChange = new EventEmitter<string>();

  open = false;
  query = '';

  get selected(): CountrySelectItem | null {
    return this.countries.find((c) => c.code === this.value) ?? null;
  }

  get filtered(): CountrySelectItem[] {
    const q = this.normalize(this.query.trim());
    if (!q) return this.countries;
    return this.countries.filter(
      (c) =>
        this.normalize(c.name).includes(q) ||
        c.code.toLowerCase().includes(q) ||
        (c.dialCode ?? '').includes(q),
    );
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) this.query = '';
  }

  pick(c: CountrySelectItem): void {
    this.value = c.code;
    this.valueChange.emit(c.code);
    this.open = false;
    this.query = '';
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();
    this.value = '';
    this.valueChange.emit('');
    this.open = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open = false;
  }

  /** Lowercase + strip diacritics so "peru" matches "Perú". */
  private normalize(s: string): string {
    return s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }
}
